import {
  importDb,
  getActivities,
  getFilterOptions,
  deleteActivities,
  getAllActivityIds,
} from "../lib/import-db";
import type {
  SearchParams,
  SortParams,
  FetchResult,
} from "../components/Manager/types";

export type ImportActivity = {
  id: number;
  typeId: number;
  periodId: number;
  productId: number;
  budget: number | null;
  type_name: string;
  period_reference: string;
  product_reference: string;
  product_name: string | null;
  supplier_name: string | null;
  store_count: number;
  problem: string | null;
};

export async function fetchImportActivities(
  _importId: number, // No longer needed, but kept for API compatibility
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams,
): Promise<FetchResult<ImportActivity>> {
  // Map sort column names from snake_case (UI) to camelCase (IndexedDB)
  const sortColumnMap: Record<string, string> = {
    store_count: "storeCount",
    type_name: "typeName",
  };
  const mappedSortBy = sortParams?.column
    ? (sortColumnMap[sortParams.column] ?? sortParams.column)
    : undefined;

  // Get activities from IndexedDB
  const { data: activities, totalCount } = await getActivities({
    limit,
    offset,
    periodIds: searchParams?.filters?.periods as number[] | undefined,
    productIds: searchParams?.filters?.products as number[] | undefined,
    supplierNames: searchParams?.filters?.suppliers as string[] | undefined,
    hasProblem: searchParams?.binary?.hasProblem,
    sortBy: mappedSortBy,
    sortOrder: sortParams?.order,
  });

  // Get tokens for reference lookups
  const tokens = await importDb.tokens.toArray();
  const tokenMap = new Map<number, string>();
  tokens.forEach((t) => {
    if (t.id !== undefined) {
      tokenMap.set(t.id, t.reference);
    }
  });

  // Get catalog for supplier lookups
  const productCodes = [
    ...new Set(activities.map((a) => tokenMap.get(a.productId) ?? "")),
  ].filter(Boolean);
  const catalogProducts = await importDb.catalog
    .where("code")
    .anyOf(productCodes)
    .toArray();
  const catalogByCode = new Map(catalogProducts.map((p) => [p.code, p]));

  // Map to the expected format
  const combinedActivities: ImportActivity[] = activities.map((activity) => {
    const productRef = tokenMap.get(activity.productId) ?? "Unknown";
    const catalog = catalogByCode.get(productRef);
    return {
      id: activity.id!,
      typeId: activity.typeId,
      periodId: activity.periodId,
      productId: activity.productId,
      budget: activity.budget,
      type_name: activity.typeName,
      period_reference: tokenMap.get(activity.periodId) ?? "Unknown",
      product_reference: productRef,
      product_name: catalog?.name ?? null,
      supplier_name: catalog?.supplierName ?? null,
      store_count: activity.storeCount,
      problem: activity.problem ?? null,
    };
  });

  const hasMore =
    offset !== undefined && limit !== undefined
      ? offset + limit < totalCount
      : false;

  return {
    data: combinedActivities,
    hasMore,
    totalCount,
  };
}

export async function deleteImportActivities(ids: number[]): Promise<void> {
  await deleteActivities(ids);
}

export async function fetchAllImportActivityIds(
  _importId: number, // No longer needed, kept for API compatibility
  searchParams?: SearchParams,
): Promise<number[]> {
  return getAllActivityIds({
    periodIds: searchParams?.filters?.periods as number[] | undefined,
    productIds: searchParams?.filters?.products as number[] | undefined,
    supplierNames: searchParams?.filters?.suppliers as string[] | undefined,
    hasProblem: searchParams?.binary?.hasProblem,
  });
}

export type FilterOption = {
  id: number;
  reference: string;
  exists?: boolean;
  supplierName?: string | null;
};

export type SupplierFilterOption = {
  name: string;
};

export async function fetchImportFilterOptions(): Promise<{
  periods: FilterOption[];
  products: FilterOption[];
  suppliers: SupplierFilterOption[];
}> {
  const options = await getFilterOptions();

  // Sort by reference
  options.periods.sort((a, b) => a.reference.localeCompare(b.reference));
  options.products.sort((a, b) => a.reference.localeCompare(b.reference));

  return options;
}

/**
 * Fetch distinct values for a DynamicSearch filter.
 * Returns values present in the dataset after applying other filters.
 */
export async function fetchDistinctValuesForImport(
  filterKey: string,
  otherFilters?: Record<string, unknown>,
): Promise<{ value: string | number; label: string }[]> {
  // Get activities with other filters applied (excluding the current filter)
  const { data: activities } = await getActivities({
    periodIds:
      filterKey !== "periods"
        ? (otherFilters?.periods as number[] | undefined)
        : undefined,
    productIds:
      filterKey !== "products"
        ? (otherFilters?.products as number[] | undefined)
        : undefined,
    supplierNames:
      filterKey !== "suppliers"
        ? (otherFilters?.suppliers as string[] | undefined)
        : undefined,
  });

  // Get tokens for reference lookups
  const tokens = await importDb.tokens.toArray();
  const tokenMap = new Map<number, { id: number; reference: string }>();
  tokens.forEach((t) => {
    if (t.id !== undefined) {
      tokenMap.set(t.id, { id: t.id, reference: t.reference });
    }
  });

  // Get catalog for product/supplier lookups
  const allProductCodes = tokens
    .filter((t) => t.type === "product")
    .map((t) => t.reference);
  const catalogProducts = await importDb.catalog
    .where("code")
    .anyOf(allProductCodes)
    .toArray();
  const catalogByCode = new Map(catalogProducts.map((p) => [p.code, p]));

  switch (filterKey) {
    case "periods": {
      // Get unique period IDs from filtered activities
      const periodIds = [...new Set(activities.map((a) => a.periodId))];
      return periodIds
        .map((id) => {
          const token = tokenMap.get(id);
          return token ? { value: token.id, label: token.reference } : null;
        })
        .filter((v): v is { value: number; label: string } => v !== null)
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    case "products": {
      // Get unique product IDs from filtered activities
      const productIds = [...new Set(activities.map((a) => a.productId))];
      return productIds
        .map((id) => {
          const token = tokenMap.get(id);
          if (!token) return null;
          const catalog = catalogByCode.get(token.reference);
          const label = catalog?.name
            ? `${token.reference} - ${catalog.name}`
            : token.reference;
          return { value: token.id, label };
        })
        .filter((v): v is { value: number; label: string } => v !== null)
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    case "suppliers": {
      // Get unique suppliers from filtered activities via their products
      const productIds = [...new Set(activities.map((a) => a.productId))];
      const supplierNames = new Set<string>();
      for (const id of productIds) {
        const token = tokenMap.get(id);
        if (token) {
          const catalog = catalogByCode.get(token.reference);
          if (catalog?.supplierName) {
            supplierNames.add(catalog.supplierName);
          }
        }
      }
      return Array.from(supplierNames)
        .sort()
        .map((name) => ({ value: name, label: name }));
    }

    default:
      return [];
  }
}
